import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import Feature from 'ol/Feature';
import GeoJSON from 'ol/format/GeoJSON';
import GPX from 'ol/format/GPX';
import KML from 'ol/format/KML';
import Geometry from 'ol/geom/Geometry';
import Circle from 'ol/geom/Circle';
import { fromCircle } from 'ol/geom/Polygon';
import { environment } from 'src/environments/environment';

/** GeoJSON Feature as stored in `gis_project_features` (geometry in EPSG:4326). */
export interface GisStoredFeature {
  type: 'Feature';
  id: string;
  geometry: any;
  properties: Record<string, unknown>;
  layer?: string;
  version?: number | null;
  style?: Record<string, unknown> | null;
  server_id?: number;
  updated_at?: string;
}

export interface GisStoredFeatureCollection {
  type: 'FeatureCollection';
  features: GisStoredFeature[];
  truncated: boolean;
  limit: number;
}

export interface GisSyncPayload {
  /** Default layer for upserts that do not carry their own `layer`. */
  layer?: string;
  upserts: GisStoredFeature[];
  deletes: string[];
}

export interface GisSyncResult {
  saved: { id: string; server_id: number; version: number; created: boolean }[];
  deleted: string[];
}

export interface GisLayerSummary {
  layer: string;
  feature_count: number;
  last_updated: string | null;
  extent: [number, number, number, number] | null;
}

export interface GisStoreSummary {
  project_id: number;
  total_features: number;
  layers: GisLayerSummary[];
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MAX_PROPERTY_DEPTH = 6;

export function generateUuid(): string {
  const c: Crypto | undefined = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Keeps only JSON-safe plain data: drops OpenLayers objects, functions, styles and cycles. */
function toPlainValue(value: unknown, depth: number, seen: WeakSet<object>): unknown {
  if (value == null) return value;
  const t = typeof value;
  if (t === 'string' || t === 'boolean') return value;
  if (t === 'number') return Number.isFinite(value as number) ? value : null;
  if (t !== 'object' || depth > MAX_PROPERTY_DEPTH) return undefined;
  const obj = value as object;
  if (seen.has(obj)) return undefined;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    seen.add(obj);
    return value.map((v) => toPlainValue(v, depth + 1, seen)).filter((v) => v !== undefined);
  }
  const proto = Object.getPrototypeOf(obj);
  if (proto !== Object.prototype && proto !== null) return undefined;
  if ('ol_uid' in obj) return undefined;
  seen.add(obj);
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    const plain = toPlainValue(v, depth + 1, seen);
    if (plain !== undefined) out[k] = plain;
  }
  return out;
}

/**
 * Client for the project feature store (`/gis/projects/:projectId/...`).
 * The map runs in EPSG:3857; the store is always EPSG:4326, so every read and
 * write goes through {@link format}.
 */
@Injectable({ providedIn: 'root' })
export class GisFeatureStoreService {
  readonly format = new GeoJSON({ dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' });

  /** Last server version per feature UUID, sent back for optimistic concurrency. */
  private readonly versions = new Map<string, number>();
  /** Serialised layer/geometry/properties last known to match the server, per feature UUID. */
  private readonly signatures = new Map<string, string>();

  constructor(private http: HttpClient) {}

  private base(projectId: string | number): string {
    return `${environment.BASE_URL}gis/projects/${encodeURIComponent(String(projectId))}`;
  }

  load(
    projectId: string | number,
    opts: { layer?: string; bbox?: [number, number, number, number]; limit?: number } = {}
  ): Observable<GisStoredFeatureCollection> {
    let params = new HttpParams();
    if (opts.layer) params = params.set('layer', opts.layer);
    if (opts.bbox) params = params.set('bbox', opts.bbox.join(','));
    if (opts.limit) params = params.set('limit', String(opts.limit));
    return this.http
      .get<GisStoredFeatureCollection>(`${this.base(projectId)}/features`, { params })
      .pipe(tap((fc) => fc.features.forEach((f) => f.version != null && this.versions.set(f.id, f.version))));
  }

  sync(projectId: string | number, payload: GisSyncPayload): Observable<GisSyncResult> {
    return this.http.post<GisSyncResult>(`${this.base(projectId)}/features/sync`, payload).pipe(
      tap((res) => {
        res.saved.forEach((s) => this.versions.set(s.id, s.version));
        res.deleted.forEach((id) => this.forget(id));
      })
    );
  }

  deleteLayer(projectId: string | number, layerKey: string): Observable<{ layer: string; deleted_count: number }> {
    return this.http.delete<{ layer: string; deleted_count: number }>(
      `${this.base(projectId)}/layers/${encodeURIComponent(layerKey)}`
    );
  }

  summary(projectId: string | number): Observable<GisStoreSummary> {
    return this.http.get<GisStoreSummary>(`${this.base(projectId)}/summary`);
  }

  private signatureOf(f: GisStoredFeature): string {
    return JSON.stringify([f.layer ?? null, f.geometry, f.properties]);
  }

  /** True when the feature matches what was last loaded from / saved to the server. */
  isUnchanged(f: GisStoredFeature): boolean {
    return this.signatures.get(f.id) === this.signatureOf(f);
  }

  markSynced(f: GisStoredFeature): void {
    this.signatures.set(f.id, this.signatureOf(f));
  }

  forget(uid: string): void {
    this.versions.delete(uid);
    this.signatures.delete(uid);
  }

  /** Returns the feature's store UUID, assigning one via `setId` when it has none. */
  ensureUid(feature: Feature): string {
    const id = feature.getId();
    if (typeof id === 'string' && UUID_RE.test(id)) return id;
    const uid = generateUuid();
    feature.setId(uid);
    return uid;
  }

  uidOf(feature: Feature): string | null {
    const id = feature.getId();
    return typeof id === 'string' && UUID_RE.test(id) ? id : null;
  }

  /** Serialises an OpenLayers feature (EPSG:3857) to a store feature (EPSG:4326). */
  toStoredFeature(feature: Feature, layerKey: string): GisStoredFeature | null {
    let geometry = feature.getGeometry() as Geometry | undefined;
    if (!geometry) return null;
    const extra: Record<string, unknown> = {};
    if (geometry instanceof Circle) {
      extra['shape_hint'] = 'circle';
      extra['circle_radius_m'] = geometry.getRadius();
      geometry = fromCircle(geometry, 64);
    }
    const uid = this.ensureUid(feature);
    const geometryName = feature.getGeometryName();
    const props: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(feature.getProperties())) {
      if (k === geometryName || k === 'geometry' || k.startsWith('__')) continue;
      const plain = toPlainValue(v, 0, new WeakSet());
      if (plain !== undefined) props[k] = plain;
    }
    return {
      type: 'Feature',
      id: uid,
      geometry: this.format.writeGeometryObject(geometry),
      properties: { ...props, ...extra },
      layer: layerKey,
      version: this.versions.get(uid) ?? null,
    };
  }

  /** Reads store features into OpenLayers features (EPSG:3857) keeping the UUID as feature id. */
  toOlFeatures(fc: { features: GisStoredFeature[] }): Feature[] {
    return fc.features
      .filter((f) => f && f.geometry)
      .map((f) => {
        const feature = this.format.readFeature({
          type: 'Feature',
          id: f.id,
          geometry: f.geometry,
          properties: f.properties || {},
        }) as Feature;
        feature.setId(f.id);
        return feature;
      });
  }

  /**
   * Parses user-supplied GeoJSON. Coordinates outside lon/lat range are taken as
   * EPSG:3857 (legacy exports); otherwise the declared CRS or EPSG:4326 is used.
   */
  readGeoJsonText(text: string | object): Feature[] {
    const data = typeof text === 'string' ? JSON.parse(text) : text;
    const dataProjection = this.detectProjection(data);
    return this.format.readFeatures(data, { dataProjection, featureProjection: 'EPSG:3857' }) as Feature[];
  }

  /**
   * Parses a GeoJSON, KML or GPX file body into map features (EPSG:3857). Imported
   * features get no id so they are stored as new features.
   */
  readVectorFile(text: string): Feature[] {
    const body = String(text ?? '').trim();
    let features: Feature[];
    if (body.startsWith('<')) {
      const fmt = /<gpx[\s>]/i.test(body) ? new GPX() : new KML({ extractStyles: false });
      features = fmt.readFeatures(body, { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' }) as Feature[];
    } else {
      features = this.readGeoJsonText(body);
    }
    features.forEach((f) => f.setId(undefined));
    return features.filter((f) => !!f.getGeometry());
  }

  /** Serialises features (EPSG:3857) to a GeoJSON FeatureCollection string in EPSG:4326. */
  writeGeoJsonText(features: Feature[]): string {
    const plain = features.map((f) => {
      const clone = f.clone();
      clone.setId(f.getId());
      const g = clone.getGeometry();
      if (g instanceof Circle) clone.setGeometry(fromCircle(g, 64));
      const geometryName = clone.getGeometryName();
      for (const [k, v] of Object.entries(clone.getProperties())) {
        if (k === geometryName) continue;
        const pv = toPlainValue(v, 0, new WeakSet());
        if (pv === undefined) clone.unset(k, true);
        else if (pv !== v) clone.set(k, pv, true);
      }
      return clone;
    });
    return this.format.writeFeatures(plain);
  }

  private detectProjection(data: any): string {
    const crsName = String(data?.crs?.properties?.name || '');
    if (/3857|900913|3785/.test(crsName)) return 'EPSG:3857';
    let outside = false;
    const visit = (coords: any): void => {
      if (outside || !Array.isArray(coords)) return;
      if (typeof coords[0] === 'number') {
        if (Math.abs(coords[0]) > 180 || Math.abs(coords[1]) > 90) outside = true;
        return;
      }
      coords.forEach(visit);
    };
    const visitGeom = (g: any): void => {
      if (!g || outside) return;
      if (g.type === 'GeometryCollection') (g.geometries || []).forEach(visitGeom);
      else visit(g.coordinates);
    };
    const features = data?.type === 'FeatureCollection' ? data.features || [] : [data];
    for (const f of features) {
      visitGeom(f?.type === 'Feature' ? f.geometry : f);
      if (outside) break;
    }
    return outside ? 'EPSG:3857' : 'EPSG:4326';
  }
}
